import { cva, type VariantProps } from 'class-variance-authority';
import { injectGlobal } from '@emotion/css';
import type { StylesConfig, Button } from '../types/styles';
import { getButtonStyleFromData } from '../utils/styleHelpers';
import { BUTTON_VARIANT_TO_KEY, type ButtonVariant, type ButtonSize } from './constants';
import type { FontManager } from '../fonts/manager';

const injectedFonts = new Set<string>();

export const createButtonVariants = (stylesConfig: StylesConfig, fontManager: FontManager) => {
  const { buttons } = stylesConfig;
  const fontSizeUnit = buttons.fontSizeUnit || 'px';

  // Map button1/button2/button3 to primary/secondary/accent
  const buttonStyleMap = {
    primary: buttons[BUTTON_VARIANT_TO_KEY.primary],
    secondary: buttons[BUTTON_VARIANT_TO_KEY.secondary],
    accent: buttons[BUTTON_VARIANT_TO_KEY.accent],
  } as Record<ButtonVariant, Button>;

  // Generate and inject @font-face CSS for button font families
  const fontCombinations = new Set<string>();
  Object.values(buttonStyleMap).forEach((bs) => {
    if (bs.fontFamily && bs.fontWeight) {
      const fontStyle = 'normal';
      const key = `${bs.fontFamily}:${bs.fontWeight}:${fontStyle}`;
      fontCombinations.add(key);
    }
  });

  fontCombinations.forEach((key) => {
    // Skip if already injected
    if (injectedFonts.has(key)) {
      return;
    }

    const [fontFamily, fontWeight, fontStyle = 'normal'] = key.split(':');
    const fontFaceCss = fontManager.getFontFaceCss(fontFamily, fontWeight, fontStyle);
    if (fontFaceCss) {
      injectGlobal(fontFaceCss);
      injectedFonts.add(key);
    }
  });

  const generateButtonClass = (variant: ButtonVariant, size: ButtonSize) => {
    return getButtonStyleFromData(
      buttonStyleMap[variant],
      size,
      stylesConfig.colors,
      fontSizeUnit,
      'inline-block',
    );
  };

  return cva('', {
    variants: {
      variant: {
        primary: '',
        secondary: '',
        accent: '',
      },
      size: {
        small: '',
        medium: '',
        large: '',
      },
    },
    compoundVariants: [
      // Primary variants
      { variant: 'primary', size: 'small', class: generateButtonClass('primary', 'small') },
      { variant: 'primary', size: 'medium', class: generateButtonClass('primary', 'medium') },
      { variant: 'primary', size: 'large', class: generateButtonClass('primary', 'large') },
      // Secondary variants
      { variant: 'secondary', size: 'small', class: generateButtonClass('secondary', 'small') },
      { variant: 'secondary', size: 'medium', class: generateButtonClass('secondary', 'medium') },
      { variant: 'secondary', size: 'large', class: generateButtonClass('secondary', 'large') },
      // Accent variants
      { variant: 'accent', size: 'small', class: generateButtonClass('accent', 'small') },
      { variant: 'accent', size: 'medium', class: generateButtonClass('accent', 'medium') },
      { variant: 'accent', size: 'large', class: generateButtonClass('accent', 'large') },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'medium',
    },
  });
};

export type ButtonVariantProps = VariantProps<ReturnType<typeof createButtonVariants>>;
export type ButtonVariantsFunc = ReturnType<typeof createButtonVariants>;
